import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import {
  Alert, Button, Checkbox, Col, Divider, Form, Input, Radio, Result, Row, Select, Upload
} from 'antd';
import { LockOutlined, PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../constants/api';
import {
  BENEFIT_CONSULTANTS, GENDERS, HOUSEHOLD_SIZES, DEPENDENT_RELATIONSHIPS, MONTHLY_INCOMES,
  INSURANCE_CARRIERS, YES_NO, ENROLLMENT_LOCATIONS, MAX_DEPENDENTS, AGENT,
  INCOME_DISCLAIMER_INTRO, INCOME_SOURCES, INCOME_DISCLAIMER_OUTRO,
  CONSENT_INTRO, CONSENT_PURPOSES, CONSENT_OUTRO
} from './constants';
import {
  Shell, Card, Header, LogoBadge, Body, SecureNote, SectionTitle, Disclaimer,
  ConsentBox, DependentCard, SignatureBox, SubmitRow
} from './styled';

const MAX_FILE_MB = 10;
const VISITOR_KEY = 'bha_insurance_visitor';
const ENDPOINT = `${API_BASE_URL}/public/insurance-enrollment`;
const today = new Date().toISOString().slice(0, 10);

const toOptions = (list) => list.map((v) => ({ value: v, label: v }));

const visitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

const formatSsn = (v = '') => {
  const d = String(v).replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const formatPhone = (v = '') => {
  let d = String(v).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const ssnRule = (required) => ({
  validator: (_, v) => {
    const d = String(v || '').replace(/\D/g, '');
    if (!d) return required ? Promise.reject(new Error('Enter the 9-digit Social Security Number')) : Promise.resolve();
    return d.length === 9 ? Promise.resolve() : Promise.reject(new Error('Enter all 9 digits'));
  }
});

const SignatureField = ({ value, onChange, hasError }) => {
  const wrapRef = useRef(null);
  const padRef = useRef(null);
  const [width, setWidth] = useState(0);

  // Canvas pixel size must match its displayed size or strokes land offset.
  useLayoutEffect(() => {
    const measure = () => setWidth(wrapRef.current ? wrapRef.current.clientWidth : 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (padRef.current) {
      padRef.current.clear();
      onChange?.('');
    }
    // A resize clears the canvas, so the stored signature must be cleared too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const handleEnd = () => {
    const pad = padRef.current;
    if (!pad) return;
    onChange?.(pad.isEmpty() ? '' : pad.getCanvas().toDataURL('image/png'));
  };

  const clear = () => {
    padRef.current?.clear();
    onChange?.('');
  };

  return (
    <div>
      <SignatureBox ref={wrapRef} $error={hasError}>
        {width > 0 && (
          <SignatureCanvas
            ref={padRef}
            penColor="black"
            backgroundColor="white"
            canvasProps={{ width, height: 170, 'aria-label': 'Signature pad' }}
            onEnd={handleEnd}
          />
        )}
        {!value && <div className="placeholder">Sign here with your finger or mouse</div>}
      </SignatureBox>
      <div style={{ textAlign: 'right', marginTop: 6 }}>
        <Button size="small" onClick={clear}>Clear</Button>
      </div>
    </div>
  );
};

const SignatureFieldWithStatus = (props) => {
  const { status } = Form.Item.useStatus();
  return <SignatureField {...props} hasError={status === 'error'} />;
};

const InsuranceEnrollmentPage = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const linkCode = searchParams.get('t');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const viewSent = useRef(false);

  useEffect(() => {
    document.title = 'Health Insurance Enrollment | Black Heart Association';
    if (viewSent.current) return;
    viewSent.current = true;
    fetch(`${ENDPOINT}/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ t: linkCode || undefined, visitorId: visitorId() })
    }).catch(() => {});
  }, [linkCode]);

  const handleFinish = async (values) => {
    setError(null);
    const file = values.photoId?.[0]?.originFileObj;
    const rest = { ...values };
    delete rest.photoId;
    const payload = {
      ...rest,
      dependents: (rest.dependents || []).filter(Boolean),
      t: linkCode || undefined,
      visitorId: visitorId()
    };
    const body = new FormData();
    body.append('data', JSON.stringify(payload));
    body.append('photoId', file);

    setSubmitting(true);
    try {
      const res = await fetch(ENDPOINT, { method: 'POST', body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        if (res.status === 413) throw new Error(`Your photo ID is too large. Please upload a file under ${MAX_FILE_MB} MB.`);
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }
      setDone(data.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <Header>
      <LogoBadge><img src="/bha-logo-full.png" alt="Black Heart Association" /></LogoBadge>
      <h1>Major Medical Health Insurance</h1>
      <p>Quick Start enrollment with {AGENT.company}, in partnership with Black Heart Association.</p>
    </Header>
  );

  if (done) {
    return (
      <Shell>
        <Card>
          {header}
          <Body>
            <Result
              status="success"
              title="You're all set"
              subTitle={(
                <>
                  Thank you. {AGENT.company} will contact you about your enrollment.
                  Your reference number is <strong>{done.referenceCode}</strong>.
                  <br />
                  Questions? Call <a href={AGENT.phoneHref}>{AGENT.phone}</a>.
                </>
              )}
            />
          </Body>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        {header}
        <Body>
          <SecureNote>
            <LockOutlined style={{ marginTop: 3 }} />
            <span>
              Your information is encrypted and only used to enroll you and your household in coverage
              through {AGENT.company}.
            </span>
          </SecureNote>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            autoComplete="off"
            requiredMark
          >
            <Form.Item name="benefitConsultant" label="Benefit Consultant" rules={[{ required: true, message: 'Select a benefit consultant' }]}>
              <Select showSearch placeholder="Please select" options={toOptions(BENEFIT_CONSULTANTS)} optionFilterProp="label" />
            </Form.Item>

            <SectionTitle>About you</SectionTitle>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item name="firstName" label="First Name" rules={[{ required: true, whitespace: true, message: 'Enter your first name' }]}>
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="lastName" label="Last Name" rules={[{ required: true, whitespace: true, message: 'Enter your last name' }]}>
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="dateOfBirth" label="Date of Birth" rules={[{ required: true, message: 'Enter your date of birth' }]}>
                  <Input type="date" max={today} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Select a gender' }]}>
                  <Select placeholder="Please select" options={toOptions(GENDERS)} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="ssn"
                  label="Full Social Security Number"
                  required
                  rules={[ssnRule(true)]}
                  getValueFromEvent={(e) => formatSsn(e.target.value)}
                >
                  <Input inputMode="numeric" placeholder="XXX-XX-XXXX" maxLength={11} />
                </Form.Item>
              </Col>
            </Row>

            <SectionTitle>Address</SectionTitle>
            <Form.Item name="street1" label="Street Address" rules={[{ required: true, whitespace: true, message: 'Enter your street address' }]}>
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="street2" label="Street Address Line 2">
              <Input maxLength={200} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} sm={10}>
                <Form.Item name="city" label="City" rules={[{ required: true, whitespace: true, message: 'Enter your city' }]}>
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={7}>
                <Form.Item name="state" label="State / Province" rules={[{ required: true, whitespace: true, message: 'Enter your state' }]}>
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={7}>
                <Form.Item name="zip" label="Postal / Zip Code" rules={[{ required: true, pattern: /^\d{5}(-\d{4})?$/, message: 'Enter a 5-digit ZIP' }]}>
                  <Input inputMode="numeric" maxLength={10} />
                </Form.Item>
              </Col>
            </Row>

            <SectionTitle>Household</SectionTitle>
            <Form.Item
              name="householdSize"
              label="Household Size"
              extra="If more than the member, please add the spouse and children below."
              rules={[{ required: true, type: 'array', min: 1, message: 'Select your household size' }]}
            >
              <Checkbox.Group options={toOptions(HOUSEHOLD_SIZES)} />
            </Form.Item>

            <Form.List name="dependents">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }, i) => (
                    <DependentCard key={key}>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        style={{ position: 'absolute', top: 8, right: 8 }}
                        aria-label={`Remove household member ${i + 1}`}
                      />
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Household member {i + 1}</div>
                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item name={[name, 'firstName']} label="First Name" rules={[{ required: true, whitespace: true, message: 'Required' }]}>
                            <Input maxLength={100} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name={[name, 'lastName']} label="Last Name" rules={[{ required: true, whitespace: true, message: 'Required' }]}>
                            <Input maxLength={100} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name={[name, 'relationship']} label="Relationship" rules={[{ required: true, message: 'Required' }]}>
                            <Select options={toOptions(DEPENDENT_RELATIONSHIPS)} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name={[name, 'dateOfBirth']} label="Date of Birth" rules={[{ required: true, message: 'Required' }]}>
                            <Input type="date" max={today} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name={[name, 'ssn']}
                            label="Social Security Number"
                            rules={[ssnRule(false)]}
                            getValueFromEvent={(e) => formatSsn(e.target.value)}
                          >
                            <Input inputMode="numeric" placeholder="XXX-XX-XXXX" maxLength={11} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </DependentCard>
                  ))}
                  {fields.length < MAX_DEPENDENTS && (
                    <Button icon={<PlusOutlined />} onClick={() => add({})} block style={{ marginBottom: 20 }}>
                      Add spouse or child
                    </Button>
                  )}
                </>
              )}
            </Form.List>
            <div style={{ fontSize: 13, color: '#666', marginTop: -8, marginBottom: 16 }}>
              Only add children who are NOT on a state program such as Medicaid, MediCal, or CHIP.
            </div>

            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={3} maxLength={2000} placeholder="Anything else we should know about your household?" />
            </Form.Item>

            <SectionTitle>Photo ID</SectionTitle>
            <Form.Item
              name="photoId"
              label="Upload a photo of your ID"
              required
              extra="GOV ID, DL, Military ID, Work ID, School ID, Birth Cert/ SS card, Passport, Foreign ID, Voters ID"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList || []).slice(-1)}
              rules={[{
                validator: (_, list) => {
                  const f = list?.[0]?.originFileObj;
                  if (!f) return Promise.reject(new Error('Upload a photo of your ID'));
                  if (f.size > MAX_FILE_MB * 1024 * 1024) return Promise.reject(new Error(`File must be under ${MAX_FILE_MB} MB`));
                  return Promise.resolve();
                }
              }]}
            >
              <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.heic,.heif,application/pdf">
                <Button icon={<UploadOutlined />}>Choose file or take photo</Button>
              </Upload>
            </Form.Item>

            <SectionTitle>Income and contact</SectionTitle>
            <Form.Item
              name="occupation"
              label="Member Occupation / Self-Contractor / Unemployment / Name"
              rules={[{ required: true, whitespace: true, message: 'Enter your occupation' }]}
            >
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="monthlyIncome" label="Monthly Income" rules={[{ required: true, message: 'Select a monthly income range' }]}>
              <Select placeholder="Please select" options={toOptions(MONTHLY_INCOMES)} />
            </Form.Item>
            <Disclaimer>
              <strong>IMPORTANT INCOME DISCLAIMER:</strong> {INCOME_DISCLAIMER_INTRO}
              <ul>{INCOME_SOURCES.map((s) => <li key={s}>{s}</li>)}</ul>
              {INCOME_DISCLAIMER_OUTRO}
            </Disclaimer>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  getValueFromEvent={(e) => formatPhone(e.target.value)}
                  rules={[{ required: true, pattern: /^\(\d{3}\) \d{3}-\d{4}$/, message: 'Enter a 10-digit phone number' }]}
                >
                  <Input inputMode="tel" placeholder="(000) 000-0000" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
                  <Input inputMode="email" placeholder="example@example.com" maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="officeEmail" label="Email Address (Office use only)" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
                  <Input inputMode="email" maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="doctorOrClinic" label="Name of Doctor or Clinic">
                  <Input maxLength={200} />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <SectionTitle style={{ marginTop: 0 }}>Coverage</SectionTitle>
            <Form.Item
              name="insuranceSelections"
              label="Major Medical Insurance Selection"
              rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one' }]}
            >
              <Checkbox.Group options={toOptions(INSURANCE_CARRIERS)} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item name="dentalVisionHearing" label="Dental, Vision, or Hearing" rules={[{ required: true, message: 'Required' }]}>
                  <Radio.Group options={toOptions(YES_NO)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="lifeInsurance" label="Life Insurance" rules={[{ required: true, message: 'Required' }]}>
                  <Radio.Group options={toOptions(YES_NO)} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="insuranceEnrolledName" label="Name of Insurance Enrolled" rules={[{ required: true, whitespace: true, message: 'Required' }]}>
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="enrollmentLocation" label="Enrollment Location" rules={[{ required: true, message: 'Select an enrollment location' }]}>
              <Select showSearch placeholder="Please select" options={toOptions(ENROLLMENT_LOCATIONS)} optionFilterProp="label" />
            </Form.Item>

            <SectionTitle>Consent</SectionTitle>
            <ConsentBox tabIndex={0} role="region" aria-label="Consent agreement">
              <p>{CONSENT_INTRO}</p>
              <ol>{CONSENT_PURPOSES.map((p) => <li key={p}>{p}</li>)}</ol>
              <p>{CONSENT_OUTRO}</p>
              <p style={{ marginBottom: 0 }}>
                Name of Primary Writing Agent: {AGENT.name}<br />
                Agent National Producer Number: {AGENT.npn}<br />
                Phone Number: {AGENT.phone}<br />
                Email Address: <a href={`mailto:${AGENT.email}`}>{AGENT.email}</a>
              </p>
            </ConsentBox>
            <Form.Item
              name="signature"
              label="Signature"
              rules={[{ required: true, message: 'Please sign to give your consent' }]}
            >
              <SignatureFieldWithStatus />
            </Form.Item>

            {error && <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />}

            <SubmitRow>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={submitting}
                style={{ background: '#000', borderColor: '#000' }}
              >
                Enroll
              </Button>
            </SubmitRow>
          </Form>
        </Body>
      </Card>
    </Shell>
  );
};

export default InsuranceEnrollmentPage;
